import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';
import ProvSetup from './components/ProvSetup';
import ProvPinDialog from './components/ProvPinDialog';
import StudentSetList from './components/StudentSetList';
import StudentQuiz from './components/StudentQuiz';
import PracticeSession from './components/PracticeSession';
import AdminPanel from './components/AdminPanel';
import {
  hasProvToken,
  getSet,
  getAdminStatus,
  testConnection,
  clearProvToken,
  isProvTokenAuthError,
  ProvSetStudent,
} from './provApi';

const BRAND_COLOR = '#1a365d';

type TabValue = 'student' | 'admin';
type StudentMode = 'list' | 'practice' | 'exam';

export default function ProvPage() {
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [setupReason, setSetupReason] = useState<'stale' | 'disconnected' | null>(null);
  const [currentTab, setCurrentTab] = useState<TabValue>('student');
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [studentMode, setStudentMode] = useState<StudentMode>('list');
  const [activeSet, setActiveSet] = useState<ProvSetStudent | null>(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!hasProvToken()) {
        if (!cancelled) setIsConfigured(false);
        return;
      }
      const isValid = await testConnection();
      if (cancelled) return;
      if (isValid) {
        setIsConfigured(true);
      } else {
        clearProvToken();
        setSetupReason('stale');
        setIsConfigured(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const checkAdminStatus = useCallback(async () => {
    try {
      const status = await getAdminStatus();
      setAdminUnlocked(status.isAdmin);
    } catch (error) {
      if (isProvTokenAuthError(error)) {
        clearProvToken();
        setSetupReason('stale');
        setIsConfigured(false);
      }
      setAdminUnlocked(false);
    }
  }, []);

  useEffect(() => {
    if (isConfigured) {
      checkAdminStatus();
    }
  }, [isConfigured, checkAdminStatus]);

  const handleSetupComplete = () => {
    setSetupReason(null);
    setIsConfigured(true);
  };

  const handleDisconnect = () => {
    clearProvToken();
    setAdminUnlocked(false);
    setCurrentTab('student');
    setActiveSet(null);
    setStudentMode('list');
    setSetupReason('disconnected');
    setIsConfigured(false);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    if (newValue === 'admin') {
      if (adminUnlocked) {
        setCurrentTab('admin');
      } else {
        setShowPinDialog(true);
      }
    } else {
      setCurrentTab('student');
      setActiveSet(null);
      setStudentMode('list');
    }
  };

  const handlePinSuccess = () => {
    setAdminUnlocked(true);
    setShowPinDialog(false);
    setCurrentTab('admin');
  };

  const openSet = async (setId: string, mode: 'practice' | 'exam') => {
    try {
      const set = await getSet(setId);
      setActiveSet(set);
      setStudentMode(mode);
    } catch (error) {
      if (isProvTokenAuthError(error)) {
        handleDisconnect();
      }
    }
  };

  if (isConfigured === null) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: BRAND_COLOR,
        }}
      >
        <Typography variant="h6" sx={{ color: 'white' }}>
          Laddar...
        </Typography>
      </Box>
    );
  }

  if (!isConfigured) {
    return <ProvSetup onSetupComplete={handleSetupComplete} reason={setupReason} />;
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#F5F7FA',
        overflow: 'hidden',
        pt: 'env(safe-area-inset-top)',
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <Box
        sx={{
          bgcolor: BRAND_COLOR,
          color: 'white',
          py: 2,
          px: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SchoolIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
              }}
            >
              Provstationen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
              Öva först, gör prov när du är redo
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'center',
          py: { xs: 1, sm: 1.5 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          px: { xs: 1, sm: 0 },
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            bgcolor: '#F0F2F5',
            borderRadius: 3,
            p: 0.5,
            minHeight: { xs: 44, sm: 48 },
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: { xs: 40, sm: 44 },
              minWidth: { xs: 100, sm: 120 },
              borderRadius: 2.5,
              px: { xs: 1.5, sm: 3 },
              mx: 0.5,
              fontSize: { xs: '0.8rem', sm: '0.95rem' },
              fontWeight: 500,
              textTransform: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: 'white',
                color: 'text.primary',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <Tab
            value="student"
            label="Provstationen"
            icon={<SchoolIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
          />
          <Tab
            value="admin"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Föräldraläge
                {!adminUnlocked && <LockIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
              </Box>
            }
            icon={<AdminPanelSettingsIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 800 }}>
          {currentTab === 'student' && studentMode === 'list' && (
            <StudentSetList
              onPractice={(setId) => openSet(setId, 'practice')}
              onExam={(setId) => openSet(setId, 'exam')}
            />
          )}
          {currentTab === 'student' && studentMode === 'practice' && activeSet && (
            <PracticeSession
              set={activeSet}
              onBack={() => {
                setActiveSet(null);
                setStudentMode('list');
              }}
            />
          )}
          {currentTab === 'student' && studentMode === 'exam' && activeSet && (
            <StudentQuiz
              set={activeSet}
              onBack={() => {
                setActiveSet(null);
                setStudentMode('list');
              }}
              onComplete={() => {
                setActiveSet(null);
                setStudentMode('list');
              }}
            />
          )}
          {currentTab === 'admin' && adminUnlocked && (
            <AdminPanel
              onLogout={() => {
                setAdminUnlocked(false);
                setCurrentTab('student');
              }}
              onDisconnect={handleDisconnect}
            />
          )}
        </Box>
      </Box>

      <ProvPinDialog
        open={showPinDialog}
        onClose={() => setShowPinDialog(false)}
        onSuccess={handlePinSuccess}
      />
    </Box>
  );
}
